import { NgModule, APP_INITIALIZER, ErrorHandler, Injector } from '@angular/core';
import { MDBBootstrapModulesPro, ToastModule, ToastService } from 'ng-uikit-pro-standard';
import { CartComponent } from './cart/cart.component';
import { CheckoutComponent } from './checkout/checkout.component';
import { AppConfigService } from './shared-core/services/app-config.service';
import { SharedCoreModule } from './shared-core/shared-core.module';
import { GlobalErrorHandlerService } from './shared-core/services/global-error-handler.service';
import { LoginComponent } from './account/login/login.component';
import { ModalComponent } from './cart/shared/modal/modal.component';
import { CheckoutModule } from './checkout/checkout.module';

import { AccountModule } from './account/account.module';
import { CartModule } from './cart/cart.module';
import { ProductModule } from './product/product.module';
import { SharedModule } from './shared/shared.module';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpRequestInterceptor } from './shared-core/interceptor/http-request-interceptor';
import { CookieService } from 'ngx-cookie-service';

import { SocialAuthServiceConfig } from 'angularx-social-login';
import { GoogleLoginProvider, FacebookLoginProvider } from 'angularx-social-login';

import { createCustomElement } from '@angular/elements';
import { CsPaymentsModule } from './cspayments/cs-payments.module';
import { CSPaymentsComponent } from './cspayments/cspayments.component';
import { NgxMaskModule } from 'ngx-mask';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BundleBuilderComponent } from './cart/bundle-builder/bundle-builder.component';
import { CartCustomizationComponent } from './shared/cart-customization/cart-customization.component';
import { ProductRecommendationsComponent } from './shared/product-recommendations/product-recommendations/product-recommendations.component';
import { AddToCartConfirmationDialogComponent } from './shared/add-to-cart-confirmation-dialog/add-to-cart-confirmation-dialog.component';
import { KlaviyoService } from './shared-core/services/klaviyo.service';
import { skipWhile, take } from 'rxjs/operators';
import { FooterCopyrightComponent } from './shared/footer/footer-copyright/footer-copyright.component';
import { AddPaymentMethodDialogComponent } from './shared/add-payment-method-dialog/add-payment-method-dialog.component';
import { UpdatePaymentMethodDialogComponent } from './shared/update-payment-method-dialog/update-payment-method-dialog.component';

declare global {
  interface Window {
    klaviyoService: KlaviyoService;
    toaster: ToastService
  }
}

@NgModule({
  declarations: [
    ModalComponent
  ],
  imports: [
    CartModule,
    ProductModule,
    AccountModule,
    SharedModule,
    MDBBootstrapModulesPro.forRoot(),
    SharedCoreModule.forRoot(),
    ToastModule.forRoot({ opacity: 0.9, timeOut: 15000, extendedTimeOut: 15000 }),
    CheckoutModule,
    CsPaymentsModule,
    NgxMaskModule.forRoot(),
    AngularSvgIconModule.forRoot(),
  ],
  exports: [],
  providers: [
    CookieService,
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AppConfigService],
      useFactory: (appConfigService: AppConfigService) => {
        return async () => {
          // Make sure to return a promise!
          await appConfigService.loadAppConfig();
        };
      },
    },
    { provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              '864938285836-843i7fb4nuk7gcriflaa12obqsamn7tn.apps.googleusercontent.com'
            ),
          },
          {
            id: FacebookLoginProvider.PROVIDER_ID,
            provider: new FacebookLoginProvider('481360141874344'),
          },
        ],
        onError: (err) => {
          console.error(err);
        },
      } as SocialAuthServiceConfig,
    },
  ],
})
export class AppModule {
  constructor(private injector: Injector) {}

  ngDoBootstrap() {
    const checkoutElement = createCustomElement(CheckoutComponent, { injector: this.injector });
    customElements.define('app-checkout', checkoutElement);

    const cartElement = createCustomElement(CartComponent, { injector: this.injector });
    customElements.define('app-cart', cartElement);

    const loginElement = createCustomElement(LoginComponent, { injector: this.injector });
    customElements.define('app-login', loginElement);

    const paymentsElement = createCustomElement(CSPaymentsComponent, { injector: this.injector });
    customElements.define('app-payments', paymentsElement);

    const bundleBuilderElement = createCustomElement(BundleBuilderComponent, { injector: this.injector });
    customElements.define('app-bundle-builder', bundleBuilderElement);
    
    const cartCustomizationElement = createCustomElement(CartCustomizationComponent, { injector: this.injector });
    customElements.define('app-cart-customization-direct', cartCustomizationElement);

    const appProductRecommendations = createCustomElement(ProductRecommendationsComponent, { injector: this.injector });
    customElements.define('app-product-recommendations-direct', appProductRecommendations);

    const addToCartConfirmationDialog = createCustomElement(AddToCartConfirmationDialogComponent, { injector: this.injector });
    customElements.define('app-add-to-cart-confirmation-dialog-direct', addToCartConfirmationDialog);

    const addPaymentMethodDialog = createCustomElement(AddPaymentMethodDialogComponent, { injector: this.injector });
    customElements.define('app-add-payment-method-dialog-direct', addPaymentMethodDialog);

    const updatePaymentMethodDialogComponent = createCustomElement(UpdatePaymentMethodDialogComponent, { injector: this.injector });
    customElements.define('app-update-payment-method-dialog-direct', updatePaymentMethodDialogComponent);

    const footerCopyrightRow = createCustomElement(FooterCopyrightComponent, { injector: this.injector });
    customElements.define('app-footer-copyright', footerCopyrightRow);

    const toaster = this.injector.get(ToastService);
    window.toaster = toaster;

    // #2. Razor loads Klaviyo, and dispatches klaviyoBootstrapped event.
    window.addEventListener("klaviyoBootstrapped", () => {
      const klaviyoService = this.injector.get(KlaviyoService);
      window.klaviyoService ??= klaviyoService;

      // #3. Klaviyo Service is finished loading, and we dispatch klaviyoReady event.
      klaviyoService.init$.pipe(
        skipWhile(isInit => !isInit),
        take(1)
      ).subscribe((isInit: boolean) => {
        let klaviyoReady = new Event("klaviyoReady");
        window.dispatchEvent(klaviyoReady);
      });
    });

    // #1. Angular is loaded so we dispatch angularInit event.
    // Some code in Razor might be listening to this event.
    let angularInit = new Event("angularInit");
    window.dispatchEvent(angularInit);
  }
}
